// OTA live updates via @capgo/capacitor-updater.
//
// Flow on launch:
//   1. notifyAppReady() — tells the plugin "this bundle booted fine" so it
//      isn't rolled back on next launch.
//   2. Poll /api/app/manifest. If the server's version differs from the
//      currently-running version AND its sha256 doesn't match a bundle we
//      already downloaded, fetch the zip and call set() to schedule it for
//      the NEXT launch (we never hot-swap mid-session).
//
// On web, this is all a no-op.

import { Capacitor } from "@capacitor/core";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { API_BASE } from "../api";

const CHANNEL = "prod";

type Manifest = {
  version: string | null;
  url: string | null;
  channel?: string;
  sha256?: string;
  size?: number;
  min_native?: string | null;
  published_at?: number;
  reason?: string;
};

// CFBundleVersion / versionCode of the running native shell — used so the
// server can refuse to ship a bundle that needs a newer native runtime.
const nativeBuild = async (): Promise<string> => {
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return info.build ?? "";
  } catch {
    return "";
  }
};

export const initOta = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    // Mark the current bundle as healthy so the plugin doesn't roll back.
    await CapacitorUpdater.notifyAppReady();
  } catch (e) {
    console.log("[ota] notifyAppReady failed", e);
  }

  try {
    const current = await CapacitorUpdater.current();
    const runningVersion = current?.bundle?.version ?? "builtin";

    const native = await nativeBuild();
    const url = `${API_BASE}/api/app/manifest?channel=${encodeURIComponent(CHANNEL)}&native=${encodeURIComponent(native)}`;
    const r = await fetch(url, { headers: { accept: "application/json" } });
    if (!r.ok) return;
    const manifest = (await r.json()) as Manifest;
    if (!manifest.version || !manifest.url) {
      if (manifest.reason) console.log(`[ota] manifest withheld: ${manifest.reason}`);
      return;
    }
    if (manifest.version === runningVersion) {
      console.log(`[ota] up to date (${runningVersion})`);
      return;
    }

    console.log(`[ota] downloading ${manifest.version} (running=${runningVersion})`);
    const bundle = await CapacitorUpdater.download({
      url: manifest.url,
      version: manifest.version,
      sessionKey: manifest.sha256,
    });
    // Schedule for next launch. We don't reload mid-session.
    await CapacitorUpdater.next({ id: bundle.id });
    console.log(`[ota] queued ${manifest.version} for next launch`);
  } catch (e) {
    console.log("[ota] update check failed", e);
  }
};
